import { useCallback, useEffect, useRef, useState } from "react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import type { BootstrapDTO, ChannelDTO, ConversationDTO, MoveRequestDTO, NotificationDTO, NotificationPage, ServerEvent, UserDTO } from "@justone/shared";
import {
  inBody,
  keys,
  markNotificationsLocal,
  patchChannel,
  patchConversation,
  patchReactions,
  patchRoom,
  removeMessages,
  type RoomRef,
  roomOfMessage,
  removePending,
  upsertMessage,
  upsertUser,
} from "./queries";
import { afterCollabConnects, isWikiPagePath } from "../lib/collabGate";
import { usedPreload } from "./http";
import { removeTask, taskKeys, upsertTask } from "./tasks";
import { onWikiCommit, upsertWikiPage } from "./wiki";

export type ConnState = "connecting" | "open" | "closed";

/** 지금 화면에 보이는 채널·대화. 여기의 새 메시지는 안 읽음으로 세지 않는다. */
let viewing: RoomRef | null = null;
export function setViewingRoom(room: RoomRef | null) {
  viewing = room;
}

export function isViewing(room: RoomRef) {
  return viewing?.kind === room.kind && viewing.id === room.id && document.visibilityState === "visible";
}

function handle(qc: QueryClient, e: ServerEvent, onRevoked: () => void, onNotify?: (n: NotificationDTO) => void) {
  const me = qc.getQueryData<BootstrapDTO>(keys.bootstrap)?.me;
  switch (e.type) {
    case "message.created": {
      const room = roomOfMessage(e.message);
      // 내가 보낸 것: 먼저 그려 둔 "보내는 중" 메시지를 진짜로 바꾼다
      if (e.clientId) removePending(qc, room, e.clientId, e.message.parentId);
      upsertMessage(qc, e.message);
      const mine = e.message.authorId === me?.id;
      if (inBody(e.message) && !mine && !isViewing(room)) {
        if (room.kind === "channel") patchChannel(qc, room.id, (c) => (c.joined ? { unread: c.unread + 1 } : {}));
        else patchConversation(qc, room.id, (c) => ({ unread: c.unread + 1 }));
      }
      if (room.kind === "dm" && inBody(e.message)) patchConversation(qc, room.id, { lastMessageAt: e.message.createdAt });
      // 스레드 답글: 내가 따라가는 스레드인지는 서버가 안다 → 모아보기를 다시 받는다
      if (e.message.parentId !== null && !mine) void qc.invalidateQueries({ queryKey: keys.threads });
      break;
    }
    case "reaction.changed":
      patchReactions(qc, e.messageId, e.channelId !== null ? { kind: "channel", id: e.channelId } : { kind: "dm", id: e.conversationId! }, e.parentId, e.reactions);
      break;
    case "messages.expired":
      removeMessages(qc, { kind: "dm", id: e.conversationId }, e.ids);
      break;
    case "thread.read":
      void qc.invalidateQueries({ queryKey: keys.threads });
      break;
    case "notification.created":
      qc.setQueryData<NotificationPage>(keys.notifications, (p) =>
        p && !p.notifications.some((n) => n.id === e.notification.id) ? { ...p, notifications: [e.notification, ...p.notifications], unread: p.unread + 1 } : p,
      );
      onNotify?.(e.notification);
      break;
    case "notifications.read":
      markNotificationsLocal(qc, e.ids);
      break;
    case "message.updated":
    case "message.deleted":
      upsertMessage(qc, e.message);
      break;
    case "read":
    case "conversation.read": {
      const room: RoomRef = e.type === "read" ? { kind: "channel", id: e.channelId } : { kind: "dm", id: e.conversationId };
      patchRoom(qc, room, (r) => ((r.lastReadMessageId ?? 0) <= e.messageId ? { unread: 0, lastReadMessageId: e.messageId } : {}));
      break;
    }
    case "conversation.upserted":
      qc.setQueryData<ConversationDTO[]>(keys.conversations, (list) => {
        if (!list) return list;
        return list.some((c) => c.id === e.conversation.id)
          ? list.map((c) => (c.id === e.conversation.id ? { ...c, ...e.conversation } : c))
          : [{ ...e.conversation, unread: 0, lastReadMessageId: null }, ...list];
      });
      break;
    case "move.updated":
      qc.setQueryData<MoveRequestDTO[]>(keys.moves(e.request.conversationId), (list) =>
        list ? (list.some((r) => r.id === e.request.id) ? list.map((r) => (r.id === e.request.id ? e.request : r)) : [e.request, ...list]) : list,
      );
      break;
    case "channel.upserted":
      qc.setQueryData<ChannelDTO[]>(keys.channels, (list) => {
        if (!list) return list;
        const exists = list.some((c) => c.id === e.channel.id);
        const next = exists
          ? list.map((c) => (c.id === e.channel.id ? { ...c, ...e.channel } : c))
          : [...list, { ...e.channel, joined: false, unread: 0, lastReadMessageId: null, notifyLevel: "mention" as const }];
        return next.sort((a, b) => a.name.localeCompare(b.name, "ko"));
      });
      break;
    case "channel.deleted":
      qc.setQueryData<ChannelDTO[]>(keys.channels, (list) => list?.filter((c) => c.id !== e.channelId));
      qc.removeQueries({ queryKey: keys.messages({ kind: "channel", id: e.channelId }) });
      break;
    case "channel.membership":
      patchChannel(qc, e.channelId, e.userId === me?.id ? { memberCount: e.memberCount, joined: e.joined, unread: 0 } : { memberCount: e.memberCount });
      break;
    case "user.upserted":
      upsertUser(qc, e.user);
      break;
    case "presence":
      qc.setQueryData<UserDTO[]>(keys.users, (list) => list?.map((u) => (u.id === e.userId ? { ...u, online: e.online } : u)));
      break;
    case "task.upserted":
      upsertTask(qc, e.task);
      break;
    case "task.deleted":
      removeTask(qc, e.taskId, e.parentId);
      break;
    case "labels.changed":
      void qc.invalidateQueries({ queryKey: taskKeys.labels });
      void qc.invalidateQueries({ queryKey: taskKeys.labelEvents });
      break;
    case "lists.changed":
      void qc.invalidateQueries({ queryKey: taskKeys.lists });
      break;
    case "filters.changed":
      void qc.invalidateQueries({ queryKey: taskKeys.filters });
      break;
    case "wiki.page":
      upsertWikiPage(qc, e.page);
      break;
    case "wiki.commit":
      onWikiCommit(qc, e.pageId, e.spaceId);
      break;
    case "drafts.changed":
      void qc.invalidateQueries({ queryKey: ["drafts"] });
      break;
    case "scheduled.changed":
      void qc.invalidateQueries({ queryKey: ["scheduled"] });
      break;
    case "session.revoked":
      onRevoked();
      break;
  }
}

/**
 * 로그인한 동안 WebSocket 하나를 유지한다. 끊기면 점점 간격을 늘려 다시 연결하고,
 * 다시 연결되면 놓쳤을 수 있는 변경을 서버에서 새로 받는다.
 */
export function useRealtime(onRevoked: () => void, onNotify?: (n: NotificationDTO) => void): { state: ConnState; reconnect: () => void } {
  const qc = useQueryClient();
  const [state, setState] = useState<ConnState>("connecting");
  const notifyRef = useRef(onNotify);
  notifyRef.current = onNotify;
  const reconnectRef = useRef<() => void>(() => {});

  useEffect(() => {
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let everOpened = false;
    let ping: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      setState("connecting");
      const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
      ws = new WebSocket(url);
      ws.onopen = () => {
        attempt = 0;
        setState("open");
        // 다시 연결됐거나, 문서에 담겨 온(연결 전 시점의) 데이터로 그렸으면 그사이 바뀐 것을 받는다
        if (everOpened || usedPreload()) {
          void qc.invalidateQueries({ queryKey: keys.channels });
          void qc.invalidateQueries({ queryKey: keys.conversations });
          void qc.invalidateQueries({ queryKey: keys.users });
          void qc.invalidateQueries({ queryKey: ["messages"] });
          void qc.invalidateQueries({ queryKey: keys.notifications });
          void qc.invalidateQueries({ queryKey: keys.threads });
          for (const key of ["board", "tasks", "task", "task-ref", "drafts", "scheduled", "labels", "lists", "filters", "thread", "wiki-tree", "wiki-page", "wiki-titles", "wiki-history"]) void qc.invalidateQueries({ queryKey: [key] });
        }
        everOpened = true;
        ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: "ping" })), 30_000);
      };
      ws.onmessage = (msg) => {
        try {
          handle(qc, JSON.parse(String(msg.data)) as ServerEvent, onRevoked, notifyRef.current);
        } catch (err) {
          console.error("실시간 이벤트 처리 실패", err);
        }
      };
      ws.onclose = (ev) => {
        clearInterval(ping);
        if (stopped) return;
        setState("closed");
        if (ev.code === 4401) return onRevoked();
        const delay = Math.min(30_000, 1000 * 2 ** attempt++) * (0.75 + Math.random() * 0.5);
        retry = setTimeout(connect, delay);
      };
    };

    // 휴대폰에서 앱으로 돌아왔을 때, 인터넷이 다시 연결됐을 때, "지금 다시 연결"을 눌렀을 때 바로 다시 연결
    const now = () => {
      if (ws?.readyState !== WebSocket.CLOSED) return;
      clearTimeout(retry);
      attempt = 0;
      connect();
    };
    reconnectRef.current = now;
    const onVisible = () => document.visibilityState === "visible" && now();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", now);
    // 위키 페이지로 처음 열었으면 위키 연결이 먼저 (브라우저가 같은 주소의 WebSocket을 하나씩 맺는다)
    void afterCollabConnects(3000, isWikiPagePath(location.pathname)).then(() => {
      if (!stopped && !ws) connect();
    });
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(ping);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", now);
      ws?.close();
    };
  }, [qc, onRevoked]);

  const reconnect = useCallback(() => reconnectRef.current(), []);
  return { state, reconnect };
}
